import type { FieldSchemaModel, PriceLimitsModel } from '@/shared/generated'

import React, { useMemo } from 'react'
import { pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useColumnsSchemaQuery } from '@/entities/column'
import { useSettingsSchemasQuery } from '@/entities/core'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { HiddenOnContext } from '@/shared/generated'
import {
	getEnumOptions,
	getResolvedSchema,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

export const PriceLimitsView = ({
	settings,
}: {
	settings: PriceLimitsModel
}) => {
	const { t } = useTranslation()
	const columnsQuery = useColumnsSchemaQuery(HiddenOnContext.PriceLimits, {
		select(data) {
			return Object.fromEntries(
				data.map(({ name, id }: FieldSchemaModel) => [
					id,
					t(name, {
						ns: 'fields',
						defaultValue: name,
					}),
				])
			)
		},
	})
	const settingsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:price_limits'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})

	const isLoading = columnsQuery.isLoading || settingsSchema.isLoading
	const columns = columnsQuery.data
	const schema = settingsSchema.data
	const selectOptions = useMemo(() => {
		const conflictOptions = getEnumOptions(
			prop('properties.extra.properties.on_conflict', schema)
		).map((option) => [option.value, option.label])
		const typeOptions = getEnumOptions(
			prop('properties.lower.items.properties.type', schema)
		).map((option) => [option.value, option.label])
		const violationOptions = getEnumOptions(
			prop('properties.lower.items.properties.on_violation', schema)
		).map((option) => [option.value, option.label])

		return {
			on_conflict: Object.fromEntries(conflictOptions),
			on_violation: Object.fromEntries(violationOptions),
			type: Object.fromEntries(typeOptions),
		}
	}, [schema])

	if (isLoading) {
		return (
			<Card className='space-y-4 p-4 shadow-none'>
				<PriceLimitsViewSkeleton />
			</Card>
		)
	}

	return (
		<Card className='shadow-none'>
			<CardHeader>
				<CardTitle>{schema.title}</CardTitle>
			</CardHeader>
			<CardContent className='flex flex-col gap-4 divide-y'>
				<ErrorBoundary>
					<DescriptionList className='flex flex-col'>
						<DescriptionGroup className='flex flex-col'>
							<DescriptionTerm>{schema.properties.lower.title}</DescriptionTerm>
							{settings.lower?.length ? (
								settings.lower.map((rule) => (
									<DescriptionDetail
										key={rule.name}
										className='flex flex-wrap gap-1 font-semibold'
									>
										<span>{columns?.[rule.name]}</span>
										<span>{rule.value}%</span>
										<span>{selectOptions.type[rule?.type ?? '']}</span>
										<span>
											({selectOptions.on_violation[rule?.on_violation ?? '']})
										</span>
									</DescriptionDetail>
								))
							) : (
								<DescriptionDetail className='font-semibold'>
									-
								</DescriptionDetail>
							)}
						</DescriptionGroup>
						<DescriptionGroup className='flex flex-col'>
							<DescriptionTerm>{schema.properties.upper.title}</DescriptionTerm>
							{settings.upper?.length ? (
								settings.upper.map((rule) => (
									<DescriptionDetail
										key={rule.name}
										className='flex flex-wrap gap-1 font-semibold'
									>
										<span>{columns?.[rule.name]}</span>
										<span>{rule.value}%</span>
										<span>{selectOptions.type[rule?.type ?? '']}</span>
										<span>
											({selectOptions.on_violation[rule?.on_violation ?? '']})
										</span>
									</DescriptionDetail>
								))
							) : (
								<DescriptionDetail className='font-semibold'>
									-
								</DescriptionDetail>
							)}
						</DescriptionGroup>
					</DescriptionList>
				</ErrorBoundary>
			</CardContent>
			<CardHeader>
				<CardTitle>{schema.properties.extra.title}</CardTitle>
			</CardHeader>
			<CardContent>
				<ErrorBoundary>
					<DescriptionList className='flex flex-col'>
						<DescriptionGroup className='flex flex-col'>
							<DescriptionTerm>{schema.properties.extra.title}</DescriptionTerm>
							<DescriptionDetail className='font-semibold'>
								{selectOptions.on_conflict[settings.extra.on_conflict]}
							</DescriptionDetail>
						</DescriptionGroup>
					</DescriptionList>
				</ErrorBoundary>
			</CardContent>
		</Card>
	)
}
const PriceLimitsViewSkeleton = () => (
	<>
		<Skeleton className='h-6 w-42' />

		<div className='space-y-2'>
			<Skeleton className='h-4 w-80' />
			<Skeleton className='h-6 w-80' />
			<Skeleton className='h-6 w-80' />
		</div>
		<div className='space-y-2'>
			<Skeleton className='h-4 w-80' />
			<Skeleton className='h-6 w-96' />
		</div>
		<Skeleton className='h-6 w-36' />
		<div className='space-y-2'>
			<Skeleton className='h-4 w-42' />
			<Skeleton className='h-6 w-80' />
		</div>
	</>
)
